/**
 * Integration contracts — slice 3, "connect SES".
 *
 * This is the first slice that holds a secret belonging to someone else, and
 * every shape in this file is arranged around that one fact.
 *
 * **The rule this file enforces: the API never returns a credential.** Not
 * masked, not partial, not "for editing". {@link integrationSchema} — the read
 * shape — has no field a secret could travel in, and it is `.strict()`, so an
 * extra key is a parse error rather than a silent passthrough. That is the
 * enforcement mechanism: a service that tried to return a secret would fail
 * contract parsing on its own response, instead of depending on a reviewer
 * noticing a stray property in a mapper six months from now.
 *
 * What comes back instead is the region, the last four of the access key id,
 * and the last check result.
 *
 * The other thing this file pins down is that **credentials that authenticate
 * are not an account that can send**. `GetAccount` answers whether the account
 * is out of the SES sandbox; `ListEmailIdentities` answers whether there is
 * anything to send *from*. Both answers matter, both are carried on
 * {@link sesCheckResultSchema}, and {@link sesReadiness} derives the verdict
 * once so the API and the web app cannot disagree about what "connected" means.
 *
 * **Slice 7 added the second kind, and it is the same file for a reason.** Meta
 * WhatsApp arrives below as its own connect body, its own check result and its
 * own arm of the read shape, and {@link integrationSchema} is now a union
 * discriminated on `kind` — which is what `kind` has been carried for since
 * slice 3. The rule at the top survives the change **for both kinds**: neither
 * arm has a field a credential could travel in, and both are `.strict()`. That
 * matters more for Meta, not less: an AWS secret key signs and never leaves the
 * process, while a Meta system user token is a bearer credential sent in full on
 * every Graph request, so there is no Meta equivalent of `accessKeyIdLast4` and
 * no fragment of the token is kept anywhere.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import { integrationKindSchema, integrationStatusSchema } from '../enums.js';
import { metaTokenInfoSchema } from './meta-token.js';
import {
  metaAdsIntegrationConfigSchema,
  metaAdsIntegrationSchema,
  type MetaAdsCheckResult,
} from './meta-ads.js';

/* -------------------------------------------------------------------------- */
/* Field primitives                                                            */
/* -------------------------------------------------------------------------- */

/**
 * An AWS access key id: 16–128 upper-case letters and digits.
 *
 * Deliberately **not** anchored to `AKIA`. Long-lived IAM user keys start
 * `AKIA`, but temporary STS credentials start `ASIA`, and AWS has shipped other
 * prefixes (`ABIA`, `ACCA`) over time. Hard-coding one prefix would reject a
 * valid credential and send the customer looking for a fault that is ours.
 *
 * Not upper-cased for the caller either. A lower-cased paste is a corrupted
 * paste, and silently "fixing" it produces a key that authenticates against
 * nothing and a failure message pointing at AWS instead of at the form.
 */
export const awsAccessKeyIdSchema = z
  .string()
  .trim()
  .regex(
    /^[A-Z0-9]{16,128}$/,
    'Access key ID must be 16–128 upper-case letters and digits, e.g. AKIAIOSFODNN7EXAMPLE (temporary credentials start ASIA)',
  );

/**
 * An AWS secret access key: exactly 40 base64-ish characters.
 *
 * SECURITY: the message below describes the *shape* of a correct value and
 * never echoes the submitted one. No error produced from this schema may be
 * logged with its input, and nothing derived from it may be returned — see
 * {@link integrationSchema}, which has nowhere to put it.
 */
export const awsSecretAccessKeySchema = z
  .string()
  .trim()
  .regex(
    /^[A-Za-z0-9/+=]{40}$/,
    'Secret access key must be exactly 40 characters of letters, digits, "/", "+" or "="',
  );

/**
 * An AWS region name, e.g. `us-east-1`, `ap-south-1`, `us-gov-west-1`.
 *
 * Matched by shape rather than against a list of regions: AWS adds regions, and
 * a stale allow-list would refuse a real one. Lower-cased on the way in because
 * region names are lower-case and `US-EAST-1` is a correct answer typed loudly.
 */
export const awsRegionSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(32)
  .regex(
    /^[a-z]{2}(-[a-z]+)+-\d{1,2}$/,
    'Region must look like an AWS region, e.g. us-east-1 or ap-south-1',
  );

/**
 * The last four characters of the access key id.
 *
 * This is the whole of what a person gets back to recognise which key is
 * connected. It is not sensitive — four characters of a public identifier — and
 * it is stored in `integrations.config`, never in the sealed blob.
 */
export const accessKeyIdLast4Schema = z.string().regex(/^[A-Z0-9]{4}$/);

/* -------------------------------------------------------------------------- */
/* Connect                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Connect (or replace) a tenant's SES account.
 *
 * There is **no partial update**: `integrations` is unique on
 * `(tenant_id, kind)`, connecting again replaces what is there, and changing
 * the region means re-entering the key and the secret. A half-updated
 * credential — new region, old key — is a state worth not having, because it
 * fails at send time rather than here.
 *
 * SECURITY: `secretAccessKey` is the one field in this package that must never
 * survive the request. It goes to the envelope cipher and nowhere else: not to
 * a log line, not to an audit row, not into `integrations.config`, and not back
 * out in any response. Nothing in this file provides a route for it to.
 */
export const connectSesRequestSchema = z
  .object({
    accessKeyId: awsAccessKeyIdSchema,
    secretAccessKey: awsSecretAccessKeySchema,
    region: awsRegionSchema,
  })
  .strict();
export type ConnectSesRequest = z.infer<typeof connectSesRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Check result — what a successful test learned                               */
/* -------------------------------------------------------------------------- */

/**
 * Which transport answered a check.
 *
 * `stub` is carried all the way to the screen on purpose, and rendered there as
 * a plain "simulated — no AWS call was made". A mock mistaken for the real
 * thing has already cost this project a wasted verification; a result that
 * cannot say where it came from is how that happens again.
 */
export const SES_TRANSPORT_VALUES = ['aws', 'stub'] as const;
export const sesTransportSchema = z.enum(SES_TRANSPORT_VALUES);
export type SesTransport = z.infer<typeof sesTransportSchema>;

/**
 * The SESv2 identity types we know about, for display.
 *
 * Exported as a constant rather than used as a `z.enum` on
 * {@link sesIdentitySchema}: `identityType` arrives from AWS, and an
 * unrecognised value AWS adds later must not fail the parse of an otherwise
 * good check. A third party's vocabulary is data, not our enum.
 */
export const SES_IDENTITY_TYPE_VALUES = ['EMAIL_ADDRESS', 'DOMAIN', 'MANAGED_DOMAIN'] as const;

/**
 * One sending identity from `ListEmailIdentities`.
 *
 * `verified` is the one that decides whether a tenant can send at all;
 * `sendingEnabled` is per-identity and can be false on a verified identity that
 * AWS has paused.
 */
export const sesIdentitySchema = z
  .object({
    /** The address or domain, e.g. `hello@acmeretail.in` or `acmeretail.in`. */
    identity: z.string(),
    /** `EMAIL_ADDRESS` | `DOMAIN` | `MANAGED_DOMAIN` — see {@link SES_IDENTITY_TYPE_VALUES}. */
    identityType: z.string(),
    verified: z.boolean(),
    sendingEnabled: z.boolean(),
  })
  .strict();
export type SesIdentity = z.infer<typeof sesIdentitySchema>;

/**
 * The account's send quota, as SESv2 reports it.
 *
 * `max24HourSend` is **not** constrained to be positive: AWS uses `-1` for an
 * account with no 24-hour cap, and a `.nonnegative()` here would turn the best
 * possible answer into a parse failure. `maxSendRate` is messages per second
 * and is a float (`1`, `14`, `0.5`), not an integer.
 */
export const sesQuotaSchema = z
  .object({
    /** Messages per 24 hours. `-1` means no cap. Sandbox accounts report 200. */
    max24HourSend: z.number().finite(),
    /** Messages per second. Sandbox accounts report 1. */
    maxSendRate: z.number().finite(),
    sentLast24Hours: z.number().finite(),
  })
  .strict();
export type SesQuota = z.infer<typeof sesQuotaSchema>;

/**
 * What a **successful** check learned. Stored verbatim in
 * `integrations.last_check` and returned on the read shape.
 *
 * Two AWS calls, and **only one of them is required**:
 *   - `ListEmailIdentities` → `identities`, which is the difference between
 *     "these credentials work" and "this account has something to send from".
 *     Without it there is no check;
 *   - `GetAccount` → `productionAccess` (the sandbox flag), `sendingEnabled`,
 *     `enforcementStatus` and the quota. **Optional.**
 *
 * ## Why `GetAccount` is optional, and what its absence must not be confused with
 *
 * `ses:GetAccount` is a separate IAM permission from `ses:ListEmailIdentities`,
 * and a tenant handing Wend a narrowly-scoped key very reasonably omits it: it
 * reads account-wide posture and grants nothing a send needs. Requiring it made
 * an entirely working key unusable, and the repair — "add a permission so a
 * read-only panel can populate" — is friction for no capability. So a key that
 * cannot call it still connects.
 *
 * **The four fields it feeds are therefore `null` when it was refused, and
 * `null` is not `false`.** That distinction is the whole point and this
 * codebase has paid for losing it before: `productionAccess: false` says AWS
 * told us the account is sandboxed, and `sendingEnabled: false` says AWS has
 * paused it — both are red states a person must act on. `null` says we were not
 * allowed to look. Rendering "not allowed to look" as a red badge would put an
 * alarm on a healthy account; rendering it as green would hide a real sandbox.
 * Every consumer has to branch on three cases, which is why these are `null`
 * rather than defaulted at the edge where the shape is still honest.
 *
 * Nullable rather than lifted into a nested `account` object, deliberately:
 * this object is **stored verbatim in a jsonb column** and the schema is
 * `.strict()`, so re-nesting would make every row written by an older build
 * fail to parse — and `readSesCheck` turns a parse failure into "there is no
 * account we know can send". A tenant who changed nothing would have watched
 * their connection go amber. A boolean is valid where `boolean | null` is
 * expected, so widening reads every existing row unchanged.
 *
 * `productionAccess` is deliberately the positive form of AWS's
 * `ProductionAccessEnabled` rather than a `sandboxed` flag: it matches the
 * field it comes from, so nobody has to remember which way round the negation
 * went. {@link sesReadiness} is the only place that turns these into a verdict.
 *
 * There is nothing credential-shaped in here and there must never be. This
 * object is written to a database column, returned in responses, and shown on a
 * screen.
 */
export const sesCheckResultSchema = z
  .object({
    /** Which transport answered. `stub` is surfaced to the user, not hidden. */
    transport: sesTransportSchema,
    /**
     * AWS `ProductionAccessEnabled`. False means AWS says the account is
     * sandboxed; **`null` means `ses:GetAccount` was refused and we do not
     * know**. Never collapse the two — see the header.
     */
    productionAccess: z.boolean().nullable(),
    /**
     * Account-level sending switch. False means AWS has paused the account;
     * `null` means `ses:GetAccount` was refused and we do not know.
     */
    sendingEnabled: z.boolean().nullable(),
    /** AWS `EnforcementStatus`, e.g. `HEALTHY`, `PROBATION`, `SHUTDOWN`. */
    enforcementStatus: z.string().nullable(),
    /** `null` when `ses:GetAccount` was refused. Already nullable downstream. */
    quota: sesQuotaSchema.nullable(),
    /**
     * Every identity on the account, verified or not.
     *
     * `[]` means AWS was asked and the account genuinely has none. **`null`
     * means `ses:ListEmailIdentities` was refused**, which is a different fact
     * and must reach the screen as one: an empty list is a setup problem the
     * tenant has to fix before anything can send, and `null` is a permission
     * Wend does not have and does not need.
     */
    identities: z.array(sesIdentitySchema).nullable(),
    /**
     * True when the identity listing was cut short by the page ceiling and AWS
     * still had more to give.
     *
     * It exists so a screen cannot present a partial list as the whole account.
     * Anything derived from `identities` — the count, and the readiness verdict
     * with it — is then a lower bound rather than a fact, and has to be labelled
     * that way. Defaulted rather than required so a transport that genuinely
     * read everything need not say so.
     */
    identitiesTruncated: z.boolean().default(false),
    checkedAt: isoDateTimeSchema,
  })
  .strict();
export type SesCheckResult = z.infer<typeof sesCheckResultSchema>;

/* -------------------------------------------------------------------------- */
/* Check failure — what a failed test learned                                  */
/* -------------------------------------------------------------------------- */

/**
 * Why a check failed, in terms of **what the customer has to repair**.
 *
 * These are three different jobs, not one:
 *   - `invalid_access_key` — AWS `UnrecognizedClientException`: the key id is
 *     wrong or the key has been deleted;
 *   - `invalid_secret` — AWS `SignatureDoesNotMatch`: the key id is real, the
 *     secret paired with it is not;
 *   - `access_denied` — AWS `AccessDeniedException`: the credential is valid
 *     and lacks `ses:GetAccount` / `ses:ListEmailIdentities`. This is an IAM
 *     policy edit, not a new key;
 *   - `invalid_region` — the region does not exist or SES is not available in
 *     it;
 *   - `network` — we could not reach AWS. Nothing about the credential is known;
 *   - `unknown` — anything unmapped. It exists so an unexpected AWS error is
 *     still reported honestly rather than mislabelled as one of the above.
 *
 * "Check your credentials" is none of these repairs, which is why the mapping
 * from AWS error names to these codes is a table in the SES port rather than
 * scattered `catch` blocks.
 */
export const SES_FAILURE_CODE_VALUES = [
  'invalid_access_key',
  'invalid_secret',
  'access_denied',
  'invalid_region',
  'network',
  'unknown',
] as const;
export const sesFailureCodeSchema = z.enum(SES_FAILURE_CODE_VALUES);
export type SesFailureCode = z.infer<typeof sesFailureCodeSchema>;

/**
 * A failed check.
 *
 * SECURITY: `message` is human-readable and is persisted to
 * `integrations.last_error` and shown on screen. It carries the repair, never
 * the credential — no access key id, no secret, no signing material, and no raw
 * AWS exception body that might contain one.
 */
export const sesCheckFailureSchema = z
  .object({
    code: sesFailureCodeSchema,
    /** What to fix, in a sentence. Never contains a credential. */
    message: z.string(),
    checkedAt: isoDateTimeSchema,
    /** Which transport produced the failure. `stub` simulates each code. */
    transport: sesTransportSchema,
  })
  .strict();
export type SesCheckFailure = z.infer<typeof sesCheckFailureSchema>;

/**
 * What the Test action answers with: the check succeeded and learned this, or
 * it failed and this is why.
 *
 * A discriminated union rather than an HTTP status distinction, because a
 * rejected credential is a *result* of the test, not a failure of the request —
 * it is recorded on the row (`last_error`), audited as `integration.check_failed`
 * and rendered in the same card as a success.
 */
export const sesCheckOutcomeSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), result: sesCheckResultSchema }).strict(),
  z.object({ ok: z.literal(false), failure: sesCheckFailureSchema }).strict(),
]);
export type SesCheckOutcome = z.infer<typeof sesCheckOutcomeSchema>;

/* -------------------------------------------------------------------------- */
/* Readiness — the three ways to be "connected", derived once                  */
/* -------------------------------------------------------------------------- */

/**
 * The headline verdict on a connected SES account.
 *
 *   - `ready` — production access **and** at least one verified identity. The
 *     only green one;
 *   - `sandboxed` — the credentials work, but the account is in the SES
 *     sandbox: ~200 messages a day, a capped rate, and delivery **only to
 *     verified addresses**;
 *   - `no_verified_identity` — the credentials work and there is nothing to
 *     send *from*. Note that "nothing to send from" means no identity that is
 *     both verified **and** enabled: AWS pauses individual identities, and a
 *     verified-but-paused one sends exactly as many messages as none at all.
 *     Counting only `verified` here is a real bug that was caught in review —
 *     it put a green "Ready to send" badge on an account that could not send.
 *
 * Precedence when both faults are present: `no_verified_identity` wins, because
 * it is the harder stop — a sandboxed account with a verified identity can send
 * to verified addresses today, an account with no identity can send nothing at
 * all. The headline is not the whole story though, which is why
 * {@link SesReadiness} also carries the flags: a screen showing only the
 * headline would hide the sandbox from someone who has both problems, and
 * discovering the sandbox after a campaign reaches nobody is exactly the
 * failure this slice exists to prevent. **Render every flag that is set, not
 * just the state.**
 */
/*
 * `account_not_readable` is the fourth, and it earns its place rather than
 * being squeezed into one of the other three.
 *
 * A key may authenticate and be allowed to call neither `ses:GetAccount` nor
 * `ses:ListEmailIdentities`. That is not a fault: AccessDenied is AWS
 * *authenticating* the key and then declining the read, so the credentials are
 * proven real — Wend simply cannot see the account behind them.
 *
 * None of the other three can carry it honestly. `ready` asserts production
 * access nobody read. `no_verified_identity` asserts the account has no sender,
 * which is a setup problem the tenant must fix — and it **blocks scheduling**,
 * so using it here would refuse to send on evidence that was never gathered.
 * `sandboxed` is specifically about the sandbox, and showing sandbox copy to
 * somebody whose account left it years ago is exactly the wrong end of the
 * trade.
 *
 * It is deliberately NOT a blocker. The tenant's own words: the account is
 * live, SES is configured, and a permission Wend uses only to populate a
 * read-only panel is theirs to care about, not a reason to stop them sending.
 */
export const SES_READINESS_STATE_VALUES = [
  'ready',
  'sandboxed',
  'no_verified_identity',
  'account_not_readable',
] as const;
export const sesReadinessStateSchema = z.enum(SES_READINESS_STATE_VALUES);
export type SesReadinessState = z.infer<typeof sesReadinessStateSchema>;

/**
 * The full readiness verdict: one headline plus the facts behind it.
 *
 * `sendingEnabled` is passed through rather than folded into `state`. An
 * account AWS has paused is a fourth situation, and inventing a fourth state
 * for it here would put a verdict in the contract that the slice has not
 * designed a screen for. It is surfaced as a flag so the card can warn about it
 * without the badge claiming something it cannot support.
 */
export const sesReadinessSchema = z
  .object({
    state: sesReadinessStateSchema,
    /**
     * True when the account is still in the SES sandbox, **`null` when
     * `ses:GetAccount` was refused and we could not find out**.
     *
     * `null` is not `false`. A screen that writes `!sandboxed` gets `true` for
     * the unknown case and silently promises production access it never read.
     * Branch on `=== true` and `=== false`, or read {@link accountVisible}.
     */
    sandboxed: z.boolean().nullable(),
    /**
     * At least one identity AWS calls verified — whether or not it can send.
     * **`null` when the listing was refused**, which is not the same as `false`.
     */
    hasVerifiedIdentity: z.boolean().nullable(),
    /** `null`, never `0`, when the listing was refused. Zero is a finding. */
    verifiedIdentityCount: z.number().int().min(0).nullable(),
    /**
     * At least one identity that is verified **and** enabled — i.e. something a
     * campaign could actually be sent from. This, not
     * {@link hasVerifiedIdentity}, is what `state` keys off.
     *
     * `null` when the listing was refused. Nullable rather than defaulted to
     * `0` on purpose: "this account has no sender" is a setup problem somebody
     * must fix before anything sends, and rendering it over an account nobody
     * was allowed to read would send a tenant hunting a fault that is not there.
     */
    hasSendableIdentity: z.boolean().nullable(),
    sendableIdentityCount: z.number().int().min(0).nullable(),
    /**
     * Whether `ListEmailIdentities` answered at all.
     *
     * The identity twin of {@link accountVisible}, and the flag to branch on
     * before reading any of the four counts above.
     */
    identitiesVisible: z.boolean(),
    /**
     * AWS account-level sending switch. False means AWS has paused sending;
     * **`null` means `ses:GetAccount` was refused**. `!sendingEnabled` is a bug
     * here for the same reason it is on {@link sandboxed}.
     */
    sendingEnabled: z.boolean().nullable(),
    /**
     * Whether `GetAccount` answered at all.
     *
     * Derived rather than left to each caller to spell as
     * `check.productionAccess !== null`, because three surfaces read this and
     * the one that gets the negation backwards paints an alarm on a healthy
     * account. It is exactly `sandboxed !== null`, and it is the flag a screen
     * should branch on before reading either tri-state.
     */
    accountVisible: z.boolean(),
  })
  .strict();
export type SesReadiness = z.infer<typeof sesReadinessSchema>;

/**
 * What to tell somebody whose key cannot call `GetAccount`.
 *
 * Four words visible, the rest on demand — the Shipping Reach text rule. It is
 * deliberately not phrased as a problem to fix: the connection works, the send
 * works, and the only thing missing is a read-only panel. Somebody who wants it
 * can add the permission; somebody who does not is not being nagged.
 *
 * The sandbox sentence is the load-bearing half. An account Wend cannot see
 * into may still be sandboxed, and a sandboxed account refuses every message to
 * an address that is not verified with Amazon — so a reader has to know that
 * Wend is not the thing that would warn them about it. (The sentence used to
 * say a sandboxed account "will accept every message"; SES rejects a send to an
 * unverified recipient at the API, so that was false. Slice 20's review.)
 */
export const SES_ACCOUNT_NOT_VISIBLE_COPY = Object.freeze({
  short: 'Account details not readable.',
  detail:
    'This key can list identities but cannot call GetAccount, so Wend cannot show the sending quota or say whether the account has left the SES sandbox. Sending is unaffected. If the account is still sandboxed, Amazon will refuse every message to an address that is not verified with it — and Wend will not be able to warn you about that beforehand. Add the ses:GetAccount permission to this key if you want those details here.',
});

/**
 * The same note for a key that can call **neither** `GetAccount` nor
 * `ListEmailIdentities` — `accountVisible: false` and no identity listing.
 *
 * {@link SES_ACCOUNT_NOT_VISIBLE_COPY}'s detail opens "This key can list
 * identities", which is false for that key, and the Overview printed it directly
 * beside a tile reading "This key cannot list identities": two contradictory
 * claims about one key on one card, found by the slice 20 prove pass. `short`
 * is shared so the heading does not change with the permission; only the claim
 * about what the key can do does. The Email tab still prints the original
 * sentence for both keys and is named as open rather than changed here.
 */
export const SES_ACCOUNT_NOTHING_READABLE_COPY = Object.freeze({
  short: SES_ACCOUNT_NOT_VISIBLE_COPY.short,
  detail:
    'This key can call neither GetAccount nor ListEmailIdentities, so Wend cannot show the sending quota, which senders are verified, or whether the account has left the SES sandbox. Sending is unaffected. If the account is still sandboxed, Amazon will refuse every message to an address that is not verified with it — and Wend will not be able to warn you about that beforehand. Add the ses:GetAccount and ses:ListEmailIdentities permissions to this key if you want those details here.',
});

/**
 * Derives {@link SesReadiness} from a check result.
 *
 * **This lives in `@wend/shared` on purpose.** The verdict is read in two
 * places — the API (to set `integrations.status` and to decide, in slice 5,
 * whether a campaign may be scheduled at all) and the web card (to pick the
 * badge and which warnings to show). Two implementations of "is this account
 * green" would drift, and the way they drift is that one of them stops counting
 * the sandbox. It is a pure function of the check result, with no I/O and no
 * dependency beyond this file, so both sides can call the same one.
 *
 * It is computed, never stored — for the same reason segment counts are. AWS
 * granting production access does not notify us, so a persisted verdict is
 * wrong from the moment it is written until the next Test.
 */
export function sesReadiness(check: SesCheckResult): SesReadiness {
  const identities = check.identities;
  const identitiesVisible = identities !== null;

  const verifiedIdentityCount =
    identities === null ? null : identities.filter((identity) => identity.verified).length;
  // Verified is not the same as usable. AWS pauses individual identities, and a
  // paused one delivers nothing — so this, not the verified count, decides the
  // headline. Keeping both lets the card say "verified, but AWS has paused it",
  // which is a different repair from "verify an address".
  const sendableIdentityCount =
    identities === null
      ? null
      : identities.filter((identity) => identity.verified && identity.sendingEnabled).length;
  const hasVerifiedIdentity = verifiedIdentityCount === null ? null : verifiedIdentityCount > 0;
  const hasSendableIdentity = sendableIdentityCount === null ? null : sendableIdentityCount > 0;

  /*
   * Tri-state, and written as an explicit comparison rather than `!`.
   *
   * `!check.productionAccess` is `true` for `null`, which would report every
   * account whose `GetAccount` was refused as definitely sandboxed — an alarm
   * on an account nobody has seen, which is the mirror of the defect this repo
   * already shipped once in the other direction.
   */
  const sandboxed = check.productionAccess === null ? null : !check.productionAccess;
  const accountVisible = sandboxed !== null;

  /*
   * THE STATE IS ABOUT WHAT WE KNOW, AND UNKNOWN IS NOT `ready`.
   *
   * With no sendable identity the account cannot send whatever `GetAccount`
   * would have said, so that verdict comes first and is unaffected.
   *
   * Beyond that: `ready` asserts production access, which is a claim about a
   * field we did not read, so an invisible account cannot reach it. It cannot
   * be called `sandboxed` either — that is an equally unfounded claim in the
   * other direction, and it would put a permanent amber warning on accounts
   * that left the sandbox years ago.
   *
   * So an account whose `GetAccount` was refused but whose identities were
   * readable lands on `sandboxed`, the **conservative** reading: a sandboxed
   * account delivers only to verified identities, and every identity this check
   * did read is verified. `accountVisible` and
   * {@link SES_ACCOUNT_NOT_VISIBLE_COPY} are what let the screen say which of
   * the two it is looking at.
   *
   * ## THE ORDER BELOW IS THE WHOLE RULE, AND IT CHANGED
   *
   * `no_verified_identity` used to come first, written as `!hasSendableIdentity`
   * — which is `true` for `null`. With the listing now refusable, that single
   * `!` would have declared "this account has no identity that can send" about
   * an account nobody was permitted to look at, and **that verdict blocks
   * scheduling**. A tenant with a live, production, correctly-configured SES
   * account would have been refused a campaign and sent hunting for a missing
   * sender that was there all along.
   *
   * So the unreadable case is tested FIRST and explicitly. Only once the
   * listing is known to have happened does `hasSendableIdentity === false`
   * mean what it says.
   */
  const state: SesReadinessState = !identitiesVisible
    ? 'account_not_readable'
    : hasSendableIdentity === false
      ? 'no_verified_identity'
      : sandboxed === false
        ? 'ready'
        : 'sandboxed';

  return {
    state,
    sandboxed,
    hasVerifiedIdentity,
    verifiedIdentityCount,
    hasSendableIdentity,
    sendableIdentityCount,
    sendingEnabled: check.sendingEnabled,
    accountVisible,
    identitiesVisible,
  };
}

/**
 * Where a tenant goes to leave the sandbox.
 *
 * Kept here so the warning and the link cannot get separated: a sandbox notice
 * without the way out is a dead end, and SLICES is explicit that the
 * production-access link belongs next to the warning.
 */
export const SES_PRODUCTION_ACCESS_URL =
  'https://docs.aws.amazon.com/ses/latest/dg/request-production-access.html';

/* -------------------------------------------------------------------------- */
/* Stored, non-secret config                                                   */
/* -------------------------------------------------------------------------- */

/**
 * What lives in `integrations.config` for an SES integration — and the complete
 * list of what a connect is allowed to keep in the clear.
 *
 * Declared here, next to the contracts, for the same reason
 * `storedColumnMappingSchema` is: `@wend/db` types the jsonb column with it, so
 * the column and the wire shape cannot drift, and neither the API nor the DB
 * package re-declares it.
 *
 * `.strict()` matters more than usual here. The database carries a matching
 * `integrations_config_has_no_secret` CHECK that refuses the obvious slips at
 * the storage layer; this refuses them one layer earlier, at the shape. Between
 * them, putting a secret in `config` takes two deliberate edits in two packages
 * rather than one careless spread of a request body.
 */
export const sesIntegrationConfigSchema = z
  .object({
    region: awsRegionSchema,
    /** Last four of the access key id. The whole id is deliberately not kept. */
    accessKeyIdLast4: accessKeyIdLast4Schema,
  })
  .strict();
export type SesIntegrationConfig = z.infer<typeof sesIntegrationConfigSchema>;

/* ========================================================================== */
/* Meta WhatsApp — slice 7                                                    */
/* ========================================================================== */

/**
 * Everything below describes a **Meta WhatsApp Business** integration, and it
 * lives here rather than in the API's `core/meta` for the reason that folder
 * itself gave: it wrote these shapes as TypeScript interfaces "pending exactly
 * this change", because `integrationSchema` could not express a Meta row and no
 * screen could therefore be given one. The screen exists now, so the vocabulary
 * moves to the package both ends import — `core/meta/meta.types.ts` is aliases
 * of what follows, and `apps/web/src/lib/api/whatsapp.ts` imports it instead of
 * re-declaring it. Three copies of a check result is how a field gets added to
 * one of them and not the other two.
 *
 * **The security rule is SES's, and it is harder here.** An AWS secret key signs
 * and never travels; a Meta system user token is a **bearer** credential that is
 * sent in full on every Graph request. So there is no Meta equivalent of
 * `accessKeyIdLast4`: no fragment of a bearer token is safe to keep, and what a
 * person recognises the connection by is the display name and the phone number,
 * never a piece of the credential. Nothing below has a field the token could
 * travel in, and every shape that reaches a response is `.strict()`.
 */

/* -------------------------------------------------------------------------- */
/* Field primitives                                                            */
/* -------------------------------------------------------------------------- */

/**
 * A Meta system user token.
 *
 * Length-checked only, and deliberately: Meta has changed the shape of these
 * strings more than once, and a regex written against today's token is a form
 * that starts refusing a valid credential on a day nobody changed anything. The
 * authority on whether a token is real is Meta, which is what the check asks.
 *
 * SECURITY: the message below describes the *shape* of a correct value and never
 * echoes the submitted one. No error produced from this schema may be logged
 * with its input, and nothing derived from it may be returned — the read shapes
 * have nowhere to put it.
 */
export const metaSystemUserTokenSchema = z
  .string()
  .trim()
  .min(20, 'That is too short to be a Meta system user token');

/**
 * A WhatsApp Business Account id. Digits, from WhatsApp Manager.
 *
 * Not secret — it arrives on every webhook Meta sends us. It is still validated
 * strictly, because both ids are interpolated into a Graph URL path by the live
 * transport and a value carrying a `/` would silently become a *different*
 * request against the same token.
 *
 * **The upper bound is 24 and it is not arbitrary.** It is the bound
 * `isMetaObjectId` in the API's `core/meta` already enforces before an id
 * reaches a Graph path; a form that accepted a wider id would take a value the
 * transport then refuses as `unknown_object`, which reads as "Meta has never
 * heard of this account" for what is really "that is too long to be an id". It
 * also keeps both ids under the 32-character run that the audit writer's
 * key-material tripwire refuses, so connecting can never fail at the audit row.
 */
export const metaWabaIdSchema = z
  .string()
  .trim()
  .regex(/^\d{5,24}$/, 'A WABA id is the 15–16 digit number in WhatsApp Manager');

/**
 * The id of one phone number inside a WABA — **not** the phone number.
 *
 * The mistake everybody makes once is pasting the number itself here. The
 * message says so, because `unknown_object` from Meta three seconds later does
 * not.
 */
export const metaPhoneNumberIdSchema = z
  .string()
  .trim()
  .regex(/^\d{5,24}$/, 'A phone number id is a number Meta issues — not the phone number itself');

/* -------------------------------------------------------------------------- */
/* Connect                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Connect (or replace) a tenant's WhatsApp Business account.
 *
 * The same three rules as {@link connectSesRequestSchema}: no partial update,
 * `UNIQUE (tenant_id, kind)`, and connecting again replaces what is there.
 * Rotating the token means sending all three fields, because a half-updated
 * credential — new token, old phone number id — fails at send time rather than
 * at the form.
 *
 * SECURITY: `systemUserToken` must never survive the request. It goes to the
 * envelope cipher and to the Meta port, and nowhere else: not to a log line, not
 * to an audit row, not into `integrations.config`, and not back out in any
 * response.
 */
export const connectWhatsAppRequestSchema = z
  .object({
    /** A Meta system user token. Sealed on arrival; never read back out. */
    systemUserToken: metaSystemUserTokenSchema,
    /** The WhatsApp Business Account the templates live in. */
    wabaId: metaWabaIdSchema,
    /** The number messages are sent *from*. A Meta id, not a phone number. */
    phoneNumberId: metaPhoneNumberIdSchema,
  })
  .strict();
export type ConnectWhatsAppRequest = z.infer<typeof connectWhatsAppRequestSchema>;

/* -------------------------------------------------------------------------- */
/* Check result — what a successful test learned                               */
/* -------------------------------------------------------------------------- */

/**
 * Which transport answered a check.
 *
 * `live` rather than a vendor name, so `MetaTransport` does not read `meta`
 * twice. `stub` is carried all the way to the screen for the reason
 * {@link sesTransportSchema} carries it: a fabricated green that nobody realised
 * was fabricated has already cost this project a verification.
 */
export const META_TRANSPORT_VALUES = ['live', 'stub'] as const;
export const metaTransportSchema = z.enum(META_TRANSPORT_VALUES);
export type MetaTransport = z.infer<typeof metaTransportSchema>;

/**
 * The phone number half of a check — `GET /{phone_number_id}`.
 *
 * Every field but the id is nullable: these come off a third party's JSON, the
 * field set varies with the account's state and with the token's permissions,
 * and a missing quality rating on an otherwise good answer must not turn a
 * working connection into a parse failure.
 *
 * `qualityRating` and `codeVerificationStatus` are **not** enums, for the same
 * reason `sesIdentitySchema.identityType` is not: a value Meta adds later is
 * data, not an error. {@link metaReadiness} matches the values it knows and
 * treats the rest as unknown-but-not-bad.
 */
export const metaPhoneNumberInfoSchema = z
  .object({
    phoneNumberId: z.string(),
    /** The display name Meta approved, e.g. "Acme Retail". Null before approval. */
    verifiedName: z.string().nullable(),
    /** The number as Meta formats it. Not the id. */
    displayPhoneNumber: z.string().nullable(),
    /** `GREEN` | `YELLOW` | `RED` | anything Meta adds later. */
    qualityRating: z.string().nullable(),
    /** `VERIFIED` | `NOT_VERIFIED` | `EXPIRED` — the number's own verification. */
    codeVerificationStatus: z.string().nullable(),
    /**
     * e.g. `TIER_1K`. Conversations that may be *started* per day, not messages
     * — and since Meta's change of 8 October 2025 it is the owning **business
     * portfolio's** limit rather than this number's, on every request made in
     * v24.0 or newer. Slice 51 moved these calls to v25.0, so the answer
     * changed under a field name that did not. The screen says whose it is.
     */
    messagingLimitTier: z.string().nullable(),
    /** `CLOUD_API` | `ON_PREMISE` | `NOT_APPLICABLE`. */
    platformType: z.string().nullable(),
  })
  .strict();
export type MetaPhoneNumberInfo = z.infer<typeof metaPhoneNumberInfoSchema>;

/** The account half of a check — `GET /{waba_id}`. */
export const metaWabaInfoSchema = z
  .object({
    wabaId: z.string(),
    name: z.string().nullable(),
    /** Meta's review of the account. `REJECTED`/`DISABLED` is a hard stop. */
    accountReviewStatus: z.string().nullable(),
    timezoneId: z.string().nullable(),
    /** Business Manager verification, separate from the review above. */
    businessVerificationStatus: z.string().nullable(),
  })
  .strict();
export type MetaWabaInfo = z.infer<typeof metaWabaInfoSchema>;

/*
 * `metaTokenInfoSchema` MOVED TO `meta-token.ts` IN SLICE 40, and the move was
 * forced rather than tidy.
 *
 * `meta-ads.ts` needs the same shape — `debug_token` answers identically for an
 * ads token — and this module needs `meta-ads.ts` for the `meta_ads` arm of
 * `integrationSchema` and of the `config` and `last_check` unions below. Two
 * zod modules importing each other is not a style problem: `z.object({...})`
 * evaluates at import time, so the second module to load reads `undefined` for
 * the first's exports and zod throws from inside itself with nothing in the
 * stack naming the cycle.
 *
 * It is imported below and re-exported by the package barrel, so every consumer
 * outside this package is unaffected — they all import from `@wend/shared`.
 */

/**
 * What a **successful** check learned. Stored verbatim in
 * `integrations.last_check` and returned on the read shape.
 *
 * There is nothing credential-shaped in here and there must never be. Note in
 * particular that neither the token nor the last four characters of it are
 * present: unlike an AWS access key id, no fragment of a bearer token is safe to
 * keep.
 *
 * The `token` key is the reason migration 0013 exists. `last_check` carries a
 * database tripwire that refuses a top-level key called `token` — exactly right
 * for an SES snapshot, and it would have refused every Meta row. 0013 makes the
 * tripwire kind-aware and guards the *contents* of this object instead. Renaming
 * the field to slip past the constraint would have been the wrong repair: what
 * is worth refusing is a token, not the word.
 */
export const metaCheckResultSchema = z
  .object({
    /** Which transport answered. `stub` is surfaced to the user, not hidden. */
    transport: metaTransportSchema,
    phoneNumber: metaPhoneNumberInfoSchema,
    waba: metaWabaInfoSchema,
    token: metaTokenInfoSchema,
    checkedAt: isoDateTimeSchema,
  })
  .strict();
export type MetaCheckResult = z.infer<typeof metaCheckResultSchema>;

/* -------------------------------------------------------------------------- */
/* Check failure — what a failed test learned                                  */
/* -------------------------------------------------------------------------- */

/**
 * Why a check failed, in terms of **what the tenant has to repair**. These are
 * different jobs, not one job with different wording:
 *
 *   - `invalid_token` — Meta does not accept this token at all. Generate a new
 *     one;
 *   - `token_expired` — the token was real and has run out. The repair is not
 *     "paste it again", it is "generate a token that does not expire";
 *   - `access_denied` — the token is fine and the system user lacks
 *     `whatsapp_business_management` / `whatsapp_business_messaging`, or has not
 *     been assigned the WABA. A new token will not help; a permissions edit will;
 *   - `unknown_object` — the token is fine and one of the two **ids** is wrong.
 *     Meta answers identically for both, which is why the message names which id
 *     was being fetched;
 *   - `waba_not_approved` — everything authenticates and Meta has rejected or
 *     disabled the business account. No re-paste changes it;
 *   - `rate_limited` — Meta is throttling. Nothing needs repairing but patience;
 *   - `network` — we never reached Meta. Nothing about the credentials is known;
 *   - `unknown` — anything unmapped, reported honestly rather than mislabelled.
 *
 * "Check your credentials" is none of these repairs, which is why the mapping
 * from Meta's error codes lives in one table in the API's `core/meta`.
 */
export const META_FAILURE_CODE_VALUES = [
  'invalid_token',
  'token_expired',
  'access_denied',
  'unknown_object',
  'waba_not_approved',
  'rate_limited',
  'network',
  'unknown',
] as const;
export const metaFailureCodeSchema = z.enum(META_FAILURE_CODE_VALUES);
export type MetaFailureCode = z.infer<typeof metaFailureCodeSchema>;

/**
 * A failed check.
 *
 * SECURITY: `message` is persisted to `integrations.last_error` and shown on
 * screen. It carries the repair, never the credential — no token, no fragment of
 * one, and no raw Graph error body that might echo one back.
 */
export const metaCheckFailureSchema = z
  .object({
    code: metaFailureCodeSchema,
    /** What to fix, in a sentence. Never contains the token. */
    message: z.string(),
    checkedAt: isoDateTimeSchema,
    /** Which transport produced the failure. `stub` simulates each code. */
    transport: metaTransportSchema,
  })
  .strict();
export type MetaCheckFailure = z.infer<typeof metaCheckFailureSchema>;

/**
 * What the Test action answers with. A union rather than an HTTP status
 * distinction, for the reason {@link sesCheckOutcomeSchema} gives: a rejected
 * credential is a *result* of the test, recorded on the row and rendered in the
 * same card as a success.
 */
export const metaCheckOutcomeSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), result: metaCheckResultSchema }).strict(),
  z.object({ ok: z.literal(false), failure: metaCheckFailureSchema }).strict(),
]);
export type MetaCheckOutcome = z.infer<typeof metaCheckOutcomeSchema>;

/* -------------------------------------------------------------------------- */
/* Token expiry — the state an SES key can never be in                         */
/* -------------------------------------------------------------------------- */

/** How much life is left in the token. */
export const META_TOKEN_EXPIRY_VALUES = ['never', 'expiring', 'expired'] as const;
export const metaTokenExpiryStateSchema = z.enum(META_TOKEN_EXPIRY_VALUES);
export type MetaTokenExpiryState = z.infer<typeof metaTokenExpiryStateSchema>;

/**
 * How long before expiry a token is called `expiring`.
 *
 * Fourteen days, because generating a replacement system user token is a
 * Business-Manager errand somebody has to be free to run, and a warning that
 * arrives the morning it breaks is not a warning. A 60-day token therefore
 * spends its last quarter visibly amber.
 */
export const META_TOKEN_EXPIRY_WARNING_MS = 14 * 24 * 60 * 60 * 1_000;

/**
 * `never` | `expiring` | `expired`, from the reported expiry.
 *
 * `null` in means a token Meta says never expires — the correct kind — and that
 * is `never`, not "unknown". `debug_token` reports `expires_at: 0` for exactly
 * that case and {@link metaTokenInfoSchema} has already turned it into `null`.
 */
export function metaTokenExpiry(
  expiresAt: string | null,
  now: Date = new Date(),
): MetaTokenExpiryState {
  if (expiresAt === null) return 'never';
  const at = Date.parse(expiresAt);
  // An unparseable timestamp is Meta's problem, not a reason to claim a token is
  // about to break. Treated as "no expiry we can see coming".
  if (Number.isNaN(at)) return 'never';
  const remaining = at - now.getTime();
  if (remaining <= 0) return 'expired';
  return remaining <= META_TOKEN_EXPIRY_WARNING_MS ? 'expiring' : 'never';
}

/* -------------------------------------------------------------------------- */
/* Readiness — the verdict, derived once                                       */
/* -------------------------------------------------------------------------- */

/**
 * The headline verdict on a connected WhatsApp account.
 *
 *   - `ready` — the only green one;
 *   - `number_not_verified` — the phone number has not completed Meta's
 *     verification. Nothing can be sent from it, and the repair is in WhatsApp
 *     Manager. The WhatsApp equivalent of "no verified identity";
 *   - `quality_flagged` — Meta has rated the number `RED`. It still works, and
 *     Meta is one step from restricting it;
 *   - `waba_pending_review` — Meta has not finished reviewing the business
 *     account. This resolves itself without the tenant doing anything.
 *
 * `number_not_verified` wins outright: a number that cannot send makes every
 * other warning academic. As with {@link sesReadiness}, the headline is not the
 * whole story — **render every flag that is set, not just the state.**
 */
export const META_READINESS_STATE_VALUES = [
  'ready',
  'number_not_verified',
  'quality_flagged',
  'waba_pending_review',
] as const;
export const metaReadinessStateSchema = z.enum(META_READINESS_STATE_VALUES);
export type MetaReadinessState = z.infer<typeof metaReadinessStateSchema>;

export const metaReadinessSchema = z
  .object({
    state: metaReadinessStateSchema,
    /** The number completed Meta's code verification. */
    numberVerified: z.boolean(),
    /** Meta has rated the number `RED`. */
    qualityFlagged: z.boolean(),
    /** Meta's review of the business account has not concluded. */
    wabaPendingReview: z.boolean(),
    /** Business Manager verification is incomplete. Caps template volume. */
    businessUnverified: z.boolean(),
    tokenExpiry: metaTokenExpiryStateSchema,
  })
  .strict();
export type MetaReadiness = z.infer<typeof metaReadinessSchema>;

/**
 * Derives {@link MetaReadiness} from a check result.
 *
 * **This lives in `@wend/shared` for the reason {@link sesReadiness} does.** The
 * API reads the verdict to set `integrations.status` and to decide whether a
 * campaign may be scheduled; the card reads it to pick the badge. Two
 * implementations of "is this account green" drift, and the way they drift is
 * that one of them stops counting a warning.
 *
 * Computed, never stored: Meta approving a business account does not notify us,
 * so a persisted verdict is wrong from the moment it is written until the next
 * Test.
 *
 * The comparisons are case-folded because Meta is not consistent about case
 * across the Graph fields, and an exact-match test that silently never matches
 * is how a warning stops being shown.
 */
export function metaReadiness(result: MetaCheckResult, now: Date = new Date()): MetaReadiness {
  const fold = (value: string | null): string => (value ?? '').trim().toLowerCase();

  // Absent is not "not verified": Meta omits `code_verification_status` on some
  // account types, and treating a missing field as a fault would put a red badge
  // on a working number. Only the explicit negative counts.
  const verification = fold(result.phoneNumber.codeVerificationStatus);
  const numberVerified = verification !== 'not_verified' && verification !== 'expired';

  const qualityFlagged = fold(result.phoneNumber.qualityRating) === 'red';
  const wabaPendingReview = fold(result.waba.accountReviewStatus) === 'pending';
  const businessUnverified = fold(result.waba.businessVerificationStatus) === 'not_verified';

  const state: MetaReadinessState = !numberVerified
    ? 'number_not_verified'
    : qualityFlagged
      ? 'quality_flagged'
      : wabaPendingReview
        ? 'waba_pending_review'
        : 'ready';

  return {
    state,
    numberVerified,
    qualityFlagged,
    wabaPendingReview,
    businessUnverified,
    tokenExpiry: metaTokenExpiry(result.token.expiresAt, now),
  };
}

/* -------------------------------------------------------------------------- */
/* Stored, non-secret config                                                   */
/* -------------------------------------------------------------------------- */

/**
 * What lives in `integrations.config` for a WhatsApp integration — and the
 * complete list of what a connect is allowed to keep in the clear.
 *
 * Two ids and nothing else. There is deliberately **no `tokenLast4`**: the SES
 * config keeps four characters of a public identifier, and the Meta equivalent
 * would be four characters of a bearer credential, which is not the same trade
 * at all.
 *
 * Everything else a person recognises the account by — the display number, the
 * verified name, the quality rating, the token's expiry — is read off
 * `last_check` rather than copied here. It all comes from Meta, it changes
 * without telling us, and a stored copy is wrong from the moment the account
 * changes until the next Test.
 */
export const metaIntegrationConfigSchema = z
  .object({
    wabaId: metaWabaIdSchema,
    phoneNumberId: metaPhoneNumberIdSchema,
  })
  .strict();
export type MetaIntegrationConfig = z.infer<typeof metaIntegrationConfigSchema>;

/**
 * What `integrations.config` holds, whichever kind the row is.
 *
 * `@wend/db` types the jsonb column with this, so the column and the wire shape
 * cannot drift and neither the API nor the DB package re-declares either arm.
 */
export const integrationConfigSchema = z.union([
  sesIntegrationConfigSchema,
  metaIntegrationConfigSchema,
  metaAdsIntegrationConfigSchema,
]);
export type IntegrationConfig = z.infer<typeof integrationConfigSchema>;

/** What `integrations.last_check` holds, whichever kind the row is. */
export type IntegrationCheckSnapshot = SesCheckResult | MetaCheckResult | MetaAdsCheckResult;

/* -------------------------------------------------------------------------- */
/* Read — the redacted shape                                                   */
/* -------------------------------------------------------------------------- */

/**
 * What the API returns for a connected **SES** integration.
 *
 * Read the field list for what is *not* there: there is no `secretAccessKey`,
 * no `accessKeyId`, no `credentials`, no `config` blob that one could hide in.
 * That absence is the enforcement mechanism. Returning a secret would have to
 * add a field, and `.strict()` turns an added field into a parse error on our
 * own response — so the guarantee is checked by the contract on every request
 * rather than depending on a reviewer noticing a stray property in a mapper.
 *
 * `region` and `accessKeyIdLast4` are non-null because a row only exists
 * because somebody connected: both are written at connect time, from the
 * request, into the non-secret `integrations.config`. "Nothing connected" is
 * expressed by {@link integrationResponseSchema} answering `null`, not by
 * weakening every field on this object.
 */
export const sesIntegrationSchema = z
  .object({
    id: uuidSchema,
    kind: z.literal('ses'),
    status: integrationStatusSchema,
    /** The region the credentials were connected for. Not secret. */
    region: awsRegionSchema,
    /** Last four of the access key id — enough to recognise which key. */
    accessKeyIdLast4: accessKeyIdLast4Schema,
    /** When the last **successful** check ran. Null until one has. */
    lastVerifiedAt: isoDateTimeSchema.nullable(),
    /** Why the last check failed, in repair terms. Never a credential. */
    lastError: z.string().nullable(),
    /** Snapshot from the last successful check. Null until one has succeeded. */
    lastCheck: sesCheckResultSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();
export type SesIntegration = z.infer<typeof sesIntegrationSchema>;

/**
 * What the API returns for a connected **Meta WhatsApp** integration.
 *
 * The same guarantee, and it has to work harder. An SES row can safely hand back
 * four characters of the access key id, because an access key id is a public
 * identifier; a Meta token is a bearer credential and **no fragment of it is
 * safe to return**. So there is no `tokenLast4`, no `token`, no `credentials`,
 * and — as on the SES arm — `.strict()` means adding one is a parse error on our
 * own response rather than a review comment somebody might not leave.
 *
 * What a person recognises the account by instead is the account itself:
 *
 *   - `wabaId` and `phoneNumberId` are what was connected. Non-null, from
 *     `integrations.config`, written at connect time from the request;
 *   - `verifiedName`, `displayPhoneNumber` and `qualityRating` are what **Meta**
 *     said the last time a check succeeded, and are null until one has. They are
 *     read off `lastCheck` rather than stored beside the ids on purpose: they
 *     change at Meta without telling us, and a copy kept in `config` would be
 *     stale from the moment the account changed until the next Test — the same
 *     reason readiness is computed and never persisted;
 *   - `tokenExpiresAt` is the one field this shape exists for. A token that
 *     quietly expires is this slice's largest operational risk, and it is why
 *     `integration_status` has an `expiring` member that slice 3 could never
 *     write. Null means *either* "never expires" *or* "Meta would not say" —
 *     `lastCheck.token.introspected` is what tells those two apart, and a screen
 *     must not render the second as the first.
 */
export const metaWhatsAppIntegrationSchema = z
  .object({
    id: uuidSchema,
    kind: z.literal('meta_whatsapp'),
    status: integrationStatusSchema,
    /** The WhatsApp Business Account connected. Not secret. */
    wabaId: z.string(),
    /** The phone number id messages are sent from. Not the phone number. */
    phoneNumberId: z.string(),
    /** Meta's approved display name, as of the last successful check. */
    verifiedName: z.string().nullable(),
    /** The number as Meta prints it, as of the last successful check. */
    displayPhoneNumber: z.string().nullable(),
    /** `GREEN` | `YELLOW` | `RED`, as of the last successful check. */
    qualityRating: z.string().nullable(),
    /** Null when the token does not expire, or when Meta would not say. */
    tokenExpiresAt: isoDateTimeSchema.nullable(),
    /** When the last **successful** check ran. Null until one has. */
    lastVerifiedAt: isoDateTimeSchema.nullable(),
    /** Why the last check failed, in repair terms. Never a credential. */
    lastError: z.string().nullable(),
    /** Snapshot from the last successful check. Null until one has succeeded. */
    lastCheck: metaCheckResultSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();
export type MetaWhatsAppIntegration = z.infer<typeof metaWhatsAppIntegrationSchema>;

/**
 * A stored integration as the API is allowed to describe it — **the redacted
 * read shape**, now discriminated on `kind`.
 *
 * Slice 3 promised this file would become a union here, and it is the same
 * promise either way: neither arm has a field a credential could travel in, and
 * both are `.strict()`, so the guarantee survives the change for both kinds
 * rather than being weakened into a shape loose enough to hold two.
 *
 * A union rather than one object with everything optional, because the optional
 * version is the version that compiles when somebody puts an SES region on a
 * WhatsApp row. `kind` has carried the discriminator since slice 3 for exactly
 * this.
 */
export const integrationSchema = z.discriminatedUnion('kind', [
  sesIntegrationSchema,
  metaWhatsAppIntegrationSchema,
  /*
   * Slice 40. The third arm lives in `meta-ads.ts` rather than beside the other
   * two, and the import runs one way only — see the note where
   * `metaTokenInfoSchema` used to be defined. It keeps both properties this
   * union is for: no field a credential could travel in, and `.strict()`.
   */
  metaAdsIntegrationSchema,
]);
export type Integration = z.infer<typeof integrationSchema>;

/**
 * The integration of a given kind, or `null` when the tenant has not connected
 * one.
 *
 * `null` rather than a 404: "you have not connected SES" is the ordinary
 * starting state of the Integrations screen, not an error worth an envelope.
 */
export const integrationResponseSchema = z.object({
  integration: integrationSchema.nullable(),
});
export type IntegrationResponse = z.infer<typeof integrationResponseSchema>;

/** `/integrations/:kind`. One integration per kind per tenant. */
export const integrationParamsSchema = z.object({ kind: integrationKindSchema });
export type IntegrationParams = z.infer<typeof integrationParamsSchema>;
